import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Events from '@/hooks/events';
import actions from '../constants';

const useUpdateActiveDictionaryAttachmentProperties = () => {
  const handler = useDispatch();
  return useCallback(({ properties }) => handler({
    type: actions.UPDATE_ACTIVE_DICTIONARY_ATTACHMENT_PROPERTIES, properties,
  }), []);
};

const useShowDeleteDatasetAttachment = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ attachment }) => handler({
    type: actions.SHOW_DELETE_DATASET_ATTACHMENT, category, code, attachment,
  }), [category, code]);
};

const useShowConvertAttachmentDialog = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ attachment }) => handler({
    type: actions.SHOW_CONVERT_ATTACHMENT_DIALOG, category, code, attachment,
  }), [category, code]);
};

const useShowDownloadableConfirmationDialog = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ attachmentId, isFolder }) => handler({
    type: actions.SHOW_DOWNLOADABLE_CONFIRMATION_DIALOG, category, code, attachmentId, isFolder,
  }), [category, code]);
};

const useShowEditDatasetAttachmentDialog = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ attachment }) => handler({
    type: actions.SHOW_EDIT_DATASET_ATTACHMENT_DIALOG, category, code, attachment,
  }), [category, code]);
};

const useUpdateDatasetAttachmentMetadata = ({ category, code }) => {
  const handler = useDispatch();
  return useCallback(({ attachmentId, metadata }) => handler({
    type: actions.UPDATE_DATASET_ATTACHMENT_METADATA, category, code, attachmentId, metadata,
  }), [category, code]);
};

const useDatasetAttachmentDownload = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ attachment }) => handler({
    type: actions.DATASET_ATTACHMENT_DOWNLOAD, category, code, attachment,
  }), [category, code]);
};

const useUpdateDatasetAttachmentDescription = ({ category, code }) => {
  const handler = useDispatch();
  return useCallback(({ attachment }) => handler({
    type: actions.UPDATE_DATASET_ATTACHMENT_DESCRIPTION, category, code, attachment,
  }), [category, code]);
};

const useDatasetAttachmentsUpload = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ files }) => handler({
    type: actions.DATASET_ATTACHMENTS_UPLOAD, category, code, files,
  }), [category, code]);
};

const useDatasetAttachmentsUploadFail = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ files }) => handler({
    type: actions.DATASET_ATTACHMENTS_UPLOAD_FAIL, category, code, files,
  }), [category, code]);
};

const useDatasetAttachmentUploadRemoved = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ file }) => handler({
    type: actions.DATASET_ATTACHMENT_UPLOAD_REMOVED, category, code, file,
  }), [category, code]);
};

const useDatasetAttachmentUploadSuccess = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ file, name }) => handler({
    type: actions.DATASET_ATTACHMENT_UPLOAD_SUCCESS, category, code, file, name,
  }), [category, code]);
};

const useToggleDatasetAttachmentTypeFilter = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ attachmentType }) => handler({
    type: actions.TOGGLE_DATASET_ATTACHMENT_TYPE_FILTER, category, code, attachmentType,
  }), [category, code]);
};

const useUpdateDatasetAttachmentTextFilter = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ text }) => handler({
    type: actions.UPDATE_DATASET_ATTACHMENT_TEXT_FILTER, category, code, text,
  }), [category, code]);
};

const useToggleDatasetAttachmentColumnOrderBy = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ orderByColumn }) => handler({
    type: actions.TOGGLE_DATASET_ATTACHMENT_COLUMN_ORDER_BY, category, code, orderByColumn,
  }), [category, code]);
};

const useDeleteDatasetAttachment = () => {
  const handler = useDispatch();
  return useCallback(({ items }) => handler({
    type: actions.DELETE_DATASET_ATTACHMENT, items,
  }), []);
};

const useUpdateDatasetAttachmentProperties = () => {
  const handler = useDispatch();
  return useCallback(({ properties }) => handler({
    type: actions.UPDATE_DATASET_ATTACHMENT_PROPERTIES, properties,
  }), []);
};

const useAttachmentFolderPush = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ folder }) => handler({
    type: actions.DATASET_ATTACHMENT_FOLDER_PUSH, category, code, folder,
  }), [category, code]);
};

const useAttachmentFolderNavigate = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ segments }) => handler({
    type: actions.DATASET_ATTACHMENT_FOLDER_NAVIGATE, category, code, segments,
  }), [category, code]);
};

const useShowDeleteDatasetFolder = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ folder }) => handler({
    type: actions.SHOW_DELETE_DATASET_ATTACHMENT_FOLDER, category, code, folder,
  }), [category, code]);
};

const useCreateDatasetFolder = (category, { code }) => {
  const handler = useDispatch();
  return useCallback(({ folderName }) => handler({
    type: actions.CREATE_DATASET_ATTACHMENT_FOLDER, category, code, folderName,
  }), [category, code]);
};

const useUpdateCreateDatasetAttachmentFolderProperties = () => {
  const handler = useDispatch();
  return useCallback(({ properties }) => handler({
    type: actions.UPDATE_CREATE_DATASET_ATTACHMENT_FOLDER_PROPERTIES, properties,
  }), []);
};

const useDeleteDatasetAttachmentFolder = (folder) => {
  const handler = useDispatch();
  return useCallback(() => handler({
    type: actions.DELETE_DATASET_ATTACHMENT_FOLDER, folder,
  }), [folder]);
};

export default {
  useDatasetAttachmentDownload,
  useDatasetAttachmentsUpload,
  useDatasetAttachmentsUploadFail,
  useDatasetAttachmentUploadRemoved,
  useDatasetAttachmentUploadSuccess,
  useDeleteDatasetAttachment,
  useFetchDatasetAttachments: (category, { code }) => Events.useDispatchByCode(
    actions.FETCH_DATASET_ATTACHMENTS,
    category,
    code,
  ),
  useHideConvertToDatasetDialog: () => Events.useSimpleDispatch(actions.HIDE_CONVERT_DATAFILE_DIALOG),
  useHideDeleteDatasetAttachment: () => Events.useSimpleDispatch(actions.HIDE_DELETE_DATASET_ATTACHMENT),
  useHideDownloadableConfirmationDialog: () => Events.useSimpleDispatch(actions.HIDE_DOWNLOADABLE_CONFIRMATION_DIALOG),
  useHideEditDatasetAttachmentDialog: () => Events.useSimpleDispatch(actions.HIDE_EDIT_DATASET_ATTACHMENT_DIALOG),
  useShowConvertAttachmentDialog,
  useShowDeleteDatasetAttachment,
  useShowDownloadableConfirmationDialog,
  useShowEditDatasetAttachmentDialog,
  useToggleDatasetAttachmentColumnOrderBy,
  useToggleDatasetAttachmentTypeFilter,
  useUpdateActiveDictionaryAttachmentProperties,
  useUpdateDatasetAttachmentDescription,
  useUpdateDatasetAttachmentMetadata,
  useUpdateDatasetAttachmentProperties,
  useUpdateDatasetAttachmentTextFilter,
  useAttachmentFolderPush,
  useAttachmentFolderNavigate,
  useShowDeleteDatasetFolder,
  useHideDeleteDatasetFolder: () => Events.useSimpleDispatch(actions.HIDE_DELETE_DATASET_ATTACHMENT_FOLDER),
  useDeleteDatasetFolder: useDeleteDatasetAttachmentFolder,
  useShowCreateDatasetFolder: (category, { code }) => Events.useDispatchByCode(
    actions.SHOW_CREATE_DATASET_ATTACHMENT_FOLDER,
    category,
    code,
  ),
  useHideCreateDatasetFolder: () => Events.useSimpleDispatch(actions.HIDE_CREATE_DATASET_ATTACHMENT_FOLDER),
  useCreateDatasetFolder,
  useUpdateCreateDatasetAttachmentFolderProperties,
};
